import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Button, Row, Col, Form ,Alert } from 'react-bootstrap';
import { CSVLink } from 'react-csv';

ChartJS.register(ArcElement, Tooltip, Legend);

const PieChartComponent = ({ reportData }) => {
  const [filteredData, setFilteredData] = useState(null);
  const [filter, setFilter] = useState({
    year: new Date().getFullYear(),
    periodType: 'month',
    month: new Date().getMonth() + 1,
    quarter: Math.floor((new Date().getMonth() + 3) / 3)
  });
    const [error, setError] = useState(null);

    useEffect(() => {
      try {
        if (!reportData) {
          setError('No data available');
          return;
        }
  
        // Handle both array and object data structures
        let dataToProcess = Array.isArray(reportData) ? reportData : [reportData];
  
        const filtered = {
          categoryA: { totalAmount: 0, paid: 0, pending: 0 },
          categoryB: { totalAmount: 0, paid: 0, pending: 0 }
        };
  
        const monthsInQuarter = {
          1: [1, 2, 3],
          2: [4, 5, 6],
          3: [7, 8, 9],
          4: [10, 11, 12]
        };
  
        const timeFilter = filter.periodType === 'month' 
          ? (item) => item.year === filter.year && item.month === filter.month
          : (item) => item.year === filter.year && monthsInQuarter[filter.quarter].includes(item.month);
  
        dataToProcess.forEach(item => {
          if (timeFilter(item)) {
            filtered.categoryA.totalAmount += item.categoryA?.totalAmount || 0;
            filtered.categoryA.paid += item.categoryA?.paid || 0;
            filtered.categoryA.pending += item.categoryA?.pending || 0;
            filtered.categoryB.totalAmount += item.categoryB?.totalAmount || 0;
            filtered.categoryB.paid += item.categoryB?.paid || 0;
            filtered.categoryB.pending += item.categoryB?.pending || 0;
          }
        });
  
        setFilteredData(filtered);
        setError(null);
      } catch (err) {
        setError('Error processing data: ' + err.message);
        }
    }, [reportData, filter]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: name === 'year' || name === 'month' || name === 'quarter' 
        ? parseInt(value) 
        : value
    }));
  };

  const prepareCSVData = () => {
    if (!filteredData) return [];
    
    return [
      ['Category', 'Total Amount', 'Paid', 'Pending'],
      ['Category A', filteredData.categoryA.totalAmount, filteredData.categoryA.paid, filteredData.categoryA.pending],
      ['Category B', filteredData.categoryB.totalAmount, filteredData.categoryB.paid, filteredData.categoryB.pending],
      ['Period', `${filter.periodType === 'month' ? `Month ${filter.month}` : `Q${filter.quarter}`} ${filter.year}`]
    ];
  };

  if (error) {
      return (
        <div className="card shadow-sm h-100">
          <div className="card-header bg-white">
            <h5 className="mb-0">Category Invoices</h5>
          </div>
          <div className="card-body">
            <Alert variant="danger">{error}</Alert>
          </div>
        </div>
      );
    }
  
    if (!filteredData) return <div className="card shadow-sm h-100">Loading...</div>;
    const monthNames = [
        "January", "February", "March", "April", "May", "June", 
        "July", "August", "September", "October", "November", "December"
      ];
  return (
    <div className="card shadow-lg border-0 h-100">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Category Distribution</h5>
        <CSVLink 
          data={prepareCSVData()} 
          filename={`category-report-${filter.year}-${filter.periodType === 'month' ? filter.month : `Q${filter.quarter}`}.csv`}
          className="btn btn-sm btn-outline-success"
        >
          Export CSV
        </CSVLink>
      </div>
      <div className="card-body  border-0">
        <Row className="mb-3">
          <Col md={3}>
            <Form.Select
              name="year"
              value={filter.year}
              onChange={handleFilterChange}
            >
              {[2024, 2025].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              name="periodType"
              value={filter.periodType}
              onChange={handleFilterChange}
            >
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            {filter.periodType === 'month' ? (
             <Form.Select
             name="month"
             value={filter.month}
             onChange={handleFilterChange}
           >
             {monthNames.map((name, index) => (
               <option key={index + 1} value={index + 1}>{name}</option>
             ))}
           </Form.Select>
            ) : (
              <Form.Select
                name="quarter"
                value={filter.quarter}
                onChange={handleFilterChange}
              >
                {[1, 2, 3, 4].map(quarter => (
                  <option key={quarter} value={quarter}>Q{quarter}</option>
                ))}
              </Form.Select>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <h6>Summary for {filter.periodType === 'month' 
            ? `${new Date(filter.year, filter.month - 1).toLocaleString('default', { month: 'long' })} ${filter.year}`
            : `Q${filter.quarter} ${filter.year}`}
          </h6>
          <div className='d-flex justify-content-between'>
          <p>Category A Total: KSh {filteredData.categoryA.totalAmount.toLocaleString()}</p>
          <p>Category B Total: KSh {filteredData.categoryB.totalAmount.toLocaleString()}</p>
          </div>
        </div>

        <div style={{ height: '300px' }}>
          <Pie
            data={{
              labels: ['Category A', 'Category B'],
              datasets: [
                {
                  data: [filteredData.categoryA.totalAmount, filteredData.categoryB.totalAmount],
                  backgroundColor: ['#8e44ad', '#f39c12'],
                  hoverBackgroundColor: ['#9b59b6', '#e67e22']
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom'
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      return `${context.label}: KSh ${context.raw.toLocaleString()}`;
                    }
                  }
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PieChartComponent;